import React, { useState } from 'react';

import { Stack, Typography, Grid, FormHelperText } from '@mui/material';
import { useFinanceContext } from 'src/components/plaid/datasource';
import { getRetirementIndex } from 'src/components/plaid/forecast/utils';
import { CurrencyField } from 'src/sections/scenarios/CurrencyField';
import { PercentField } from 'src/sections/scenarios/PercentField';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { use30Years } from 'src/components/plaid/datasource-windows';
import { SlideIn } from '../SlideIn';
import { FormFooter } from './FormFooter';
import useAutoFocus from '../../../hooks/use-auto-focus';

export default function RetirementContent({ formData: _data, onNext, onBack }) {
  const { dateRange, assets, keyAssumptions } = useFinanceContext();
  const [forecastedValue, setForecastedValue] = useState(0);
  const [formData, setFormData] = useState({
    monthlySocialSecurityIncome: '',
    averageRetirementEquityWithdrawalRate: '',
    ..._data?.retirement,
  });

  const isValid = true;
  const handleNext = () => onNext({ retirement: formData });
  const handleChange = (name, value) => setFormData({ ...formData, [name]: value });

  use30Years();

  // set the IRA/401k monthly withdrawal rate based on the added accounts
  React.useEffect(() => {
    const segments = assets?.segments;
    const acc = assets?.rows?.find((e) => e.detailedCategory === '401k');
    if (!segments || !acc) return;

    const retirementIdx = getRetirementIndex(segments);
    const seg = segments[retirementIdx];
    if (seg) {
      const val = acc?.data?.[seg.label];
      if (val) setForecastedValue(val);
      // use 7.5% of the retirement account for the monthly withdrawal rate
    }
  }, [assets, dateRange, formData]);

  // multiplier to calculate the 401k withdrawal amount at the given rate
  const multiplier =
    formData.averageRetirementEquityWithdrawalRate !== undefined &&
    !Number.isNaN(formData.averageRetirementEquityWithdrawalRate)
      ? parseFloat(formData.averageRetirementEquityWithdrawalRate)
      : 0.075;

  const autoFocus = useAutoFocus();

  return (
    <>
      <Stack spacing={4} alignItems="center" justifyContent="center" textAlign="center">
        <SlideIn direction="left">
          <Typography variant="h2" color="text.secondary">
            🏝️
          </Typography>

          <Typography variant="h1">Retirement</Typography>
          <SlideIn direction="left">
            <Typography variant="h3" color="text.secondary" fontWeight="light">
              Plan for retirement, and for future financial security
            </Typography>
          </SlideIn>
        </SlideIn>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CurrencyField
              inputRef={autoFocus}
              fullWidth
              placeholder={keyAssumptions?.socialSecurityIncome ?? '3,500'}
              label={`Monthly Gross Social Security Income (at ${
                _data?.rates?.targetRetirementAge ?? 65
              })`}
              name="monthlySocialSecurityIncome"
              value={formData.monthlySocialSecurityIncome}
              onChange={(e) => handleChange('monthlySocialSecurityIncome', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <PercentField
              fullWidth
              placeholder={0.075}
              scaler={100}
              valueOffset={0}
              label={`IRA/401k Monthly Withdrawal (at ${_data?.rates?.targetRetirementAge ?? 65})`}
              name="averageRetirementEquityWithdrawalRate"
              value={formData.averageRetirementEquityWithdrawalRate}
              onChange={(e) =>
                handleChange('averageRetirementEquityWithdrawalRate', Number(e.target.value))
              }
            />

            {!Number.isNaN(forecastedValue * multiplier) && (
              <FormHelperText id="accountId-error">
                {currencyFormatter.format(forecastedValue * multiplier)} / month
              </FormHelperText>
            )}

            {/* <CurrencyField
              fullWidth
              placeholder="4,000"
              label={`IRA/401k Monthly Withdrawal (at ${_data?.rates?.targetRetirementAge})`}
              name="iraWithdrawal"
              value={formData.iraWithdrawal}
              onChange={(e) => handleChange('iraWithdrawal', e.target.value)}
            /> */}
          </Grid>
        </Grid>
      </Stack>
      <FormFooter {...{ isValid, onNext: handleNext, onBack, nextLabel: 'Finish' }} />
    </>
  );
}
